import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Badge,
    Box,
    Button,
    Checkbox,
    Heading,
    HStack,
    Icon,
    Progress,
    SimpleGrid,
    Spinner,
    Stack,
    Tag,
    TagLabel,
    Text,
    useToast,
    VStack,
} from '@chakra-ui/react';
import {
    FiActivity,
    FiAlertTriangle,
    FiCheckCircle,
    FiClipboard,
    FiDownload,
    FiDatabase,
    FiLayers,
    FiPieChart,
    FiShield,
    FiUploadCloud,
} from 'react-icons/fi';
import axios from 'axios';
import AuthContext from '../context/AuthContext';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

const DATASETS = [
    {
        type: 'products',
        title: 'Product catalog',
        description: 'Tenant product master with risk level, complexity, knowledge requirement, target market, segments, and bias mitigation scores.',
        templateName: 'products',
        downloadFilename: 'product_catalog_template.csv',
        icon: FiLayers,
        roleHint: 'Drives Portfolio Adviser and Compliance views.',
    },
    {
        type: 'holdings',
        title: 'Customer holdings',
        description: 'Position-date snapshots of each customer holding, weight, model portfolio assignment, and custodian.',
        templateName: 'holdings',
        downloadFilename: 'customer_holdings_template.csv',
        icon: FiPieChart,
        roleHint: 'Required for drift, concentration, and risk mismatch signals.',
    },
    {
        type: 'transactions',
        title: 'Customer transactions',
        description: 'Buy / sell / switch flows with channel, advised RM, recommendation id and execution-only flag.',
        templateName: 'transactions',
        downloadFilename: 'customer_transactions_template.csv',
        icon: FiActivity,
        roleHint: 'Connects RM activity, advice, and Compliance evidence.',
    },
    {
        type: 'suitability_evidence',
        title: 'Suitability evidence',
        description: 'Evidence records (KYC, risk profile, knowledge test, target market doc) with completeness, status, reviewer.',
        templateName: 'suitability_evidence',
        downloadFilename: 'suitability_evidence_template.csv',
        icon: FiShield,
        roleHint: 'Powers Compliance queues, missing-evidence signals and audit pack.',
    },
    {
        type: 'model_portfolio_targets',
        title: 'Model portfolio targets',
        description: 'Per-model min/target/max product weights with effective dates and risk profile band.',
        templateName: 'model_portfolio_targets',
        downloadFilename: 'model_portfolio_targets_template.csv',
        icon: FiClipboard,
        roleHint: 'Required for allocation drift analytics.',
    },
];

const STATUS_COLOR = {
    computed: 'green',
    inferred: 'yellow',
    unavailable: 'red',
};

const SEVERITY_COLOR = {
    critical: 'red',
    high: 'orange',
    medium: 'yellow',
    low: 'gray',
};

const formatCount = (value) => (typeof value === 'number' ? value.toLocaleString() : '–');

const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const FirmReadyDatasets = () => {
    const { token, user } = useContext(AuthContext);
    const toast = useToast();

    const [sufficiency, setSufficiency] = useState(null);
    const [signals, setSignals] = useState(null);
    const [loading, setLoading] = useState(true);
    const [signalsLoading, setSignalsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [uploadingType, setUploadingType] = useState(null);
    const [uploadResults, setUploadResults] = useState({});
    const [replaceFlags, setReplaceFlags] = useState({});
    const fileInputRefs = useRef({});

    const canUpload = user?.role === 'admin' && ['solo', 'pro', 'business'].includes(user?.subscription);
    const canSeeSignals = user?.subscription === 'business' &&
        ['admin', 'head', 'compliance', 'portfolio_adviser'].includes(user?.role);
    const canSeeSufficiency = ['solo', 'pro', 'business'].includes(user?.subscription);

    const fetchSufficiency = async () => {
        if (!token || !canSeeSufficiency) {
            setLoading(false);
            return;
        }
        try {
            const response = await axios.get(`${API_BASE_URL}/content/product-data/sufficiency`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSufficiency(response.data || null);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.error?.detail || err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchSignals = async () => {
        if (!token || !canSeeSignals) {
            setSignalsLoading(false);
            return;
        }
        try {
            const response = await axios.get(`${API_BASE_URL}/content/product-data/signals?limit=25`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSignals(response.data || null);
        } catch (err) {
            console.error('Failed to load product-data signals', err);
        } finally {
            setSignalsLoading(false);
        }
    };

    useEffect(() => {
        fetchSufficiency();
        fetchSignals();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, user?.subscription, user?.role]);

    const handleTemplateDownload = async (dataset) => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/content/product-data/templates/${dataset.templateName}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob',
                }
            );
            downloadBlob(response.data, dataset.downloadFilename);
        } catch (err) {
            toast({
                title: 'Template download failed',
                description: err.response?.data?.message || err.message,
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        }
    };

    const handleFileSelected = async (dataset, file) => {
        if (!file) {
            return;
        }
        if (!canUpload) {
            toast({
                title: 'Upload is restricted to firm admins on Solo, Team or Business',
                status: 'info',
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        const replace = Boolean(replaceFlags[dataset.type]);
        const formData = new FormData();
        formData.append('file', file);

        setUploadingType(dataset.type);
        try {
            const response = await axios.post(
                `${API_BASE_URL}/content/product-data/${dataset.type}?replace=${replace}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            const data = response.data || {};
            setUploadResults((prev) => ({ ...prev, [dataset.type]: { ...data, filename: file.name } }));
            toast({
                title: `${dataset.title} processed`,
                description: `${formatCount(data.accepted_rows)} accepted · ${formatCount(data.rejected_rows)} rejected`,
                status: data.rejected_rows > 0 ? 'warning' : 'success',
                duration: 4500,
                isClosable: true,
            });
            await fetchSufficiency();
            await fetchSignals();
        } catch (err) {
            const message = err.response?.data?.error?.detail || err.response?.data?.message || err.message;
            setUploadResults((prev) => ({
                ...prev,
                [dataset.type]: { error: message, filename: file.name },
            }));
            toast({
                title: `${dataset.title} upload failed`,
                description: message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setUploadingType(null);
            if (fileInputRefs.current[dataset.type]) {
                fileInputRefs.current[dataset.type].value = '';
            }
        }
    };

    const counts = sufficiency?.counts || {};
    const coverage = sufficiency?.coverage || {};
    const roleReadiness = useMemo(() => sufficiency?.role_readiness || [], [sufficiency]);
    const nextBestUploads = sufficiency?.next_best_uploads || [];

    if (!canSeeSufficiency) {
        return (
            <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" p={6}>
                <HStack spacing={3} mb={3}>
                    <Icon as={FiDatabase} color="brand.navy" boxSize={5} />
                    <Heading size="sm" color="gray.800">Firm-ready datasets</Heading>
                </HStack>
                <Text color="gray.600" fontSize="sm">
                    Firm-ready uploads (product catalog, holdings, transactions, suitability evidence, model portfolio targets)
                    are available on Solo, Team and Business. Start a 30-day trial to unlock dataset uploads.
                </Text>
            </Box>
        );
    }

    return (
        <Box w="full">
            <Box
                bg="linear-gradient(135deg, rgba(15,118,110,0.10) 0%, rgba(232,244,255,1) 60%, rgba(255,255,255,1) 100%)"
                borderRadius="2xl"
                border="1px solid"
                borderColor="brand.divider"
                px={{ base: 5, md: 7 }}
                py={{ base: 5, md: 6 }}
                mb={5}
            >
                <HStack spacing={3} mb={3}>
                    <Badge colorScheme="purple" borderRadius="full" px={3} py={1}>
                        Firm-ready data layer
                    </Badge>
                    <Badge variant="outline" colorScheme="gray" borderRadius="full" px={3} py={1}>
                        v2 product-data
                    </Badge>
                </HStack>
                <Heading size="md" color="gray.800" mb={2}>
                    Upload your product catalog, holdings, transactions, evidence and model targets
                </Heading>
                <Text color="gray.600" fontSize="sm" maxW="3xl">
                    These datasets replace the static catalog used in early demos. They power Portfolio Adviser drift,
                    Compliance evidence queues, and the audit-grade signals exposed to the Business dashboards.
                    Every dataset is tenant-scoped and you can replace it without redeploying.
                </Text>
            </Box>

            {error && (
                <Alert status="error" borderRadius="md" mb={5}>
                    <AlertIcon />
                    <AlertDescription fontSize="sm">{error}</AlertDescription>
                </Alert>
            )}

            {loading ? (
                <Box textAlign="center" py={6}>
                    <Spinner color="brand.teal" />
                    <Text color="gray.600" fontSize="sm" mt={3}>Loading data readiness…</Text>
                </Box>
            ) : (
                <Box
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="xl"
                    p={5}
                    mb={5}
                >
                    <HStack justify="space-between" mb={4} flexWrap="wrap" gap={2}>
                        <HStack spacing={2}>
                            <Icon as={FiCheckCircle} color="brand.navy" />
                            <Heading size="sm" color="gray.800">Data sufficiency</Heading>
                        </HStack>
                        <Text fontSize="xs" color="gray.500">
                            counts, coverage, role readiness, and next best uploads are computed live from your tenant data.
                        </Text>
                    </HStack>

                    <SimpleGrid columns={{ base: 2, md: 4, xl: 8 }} spacing={3} mb={4}>
                        {[
                            { label: 'Customers', value: counts.customers, color: 'blue' },
                            { label: 'Scored', value: counts.scored_customers, color: 'teal' },
                            { label: 'Products', value: counts.products, color: 'purple' },
                            { label: 'Holdings', value: counts.holdings, color: 'cyan' },
                            { label: 'Transactions', value: counts.transactions, color: 'orange' },
                            { label: 'Evidence', value: counts.suitability_evidence, color: 'pink' },
                            { label: 'Model targets', value: counts.model_portfolio_targets, color: 'red' },
                            { label: 'RM actions', value: counts.rm_actions, color: 'gray' },
                        ].map((item) => (
                            <Box
                                key={item.label}
                                border="1px solid"
                                borderColor="gray.100"
                                borderRadius="lg"
                                p={3}
                                bg="gray.50"
                            >
                                <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">
                                    {item.label}
                                </Text>
                                <Text fontSize="lg" fontWeight="semibold" color={`${item.color}.700`}>
                                    {formatCount(item.value)}
                                </Text>
                            </Box>
                        ))}
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                        {[
                            { key: 'scored_customer_pct', label: 'Scored customers' },
                            { key: 'customers_with_holdings_pct', label: 'Customers with holdings' },
                            { key: 'customers_with_transactions_pct', label: 'Customers with transactions' },
                            { key: 'customers_with_evidence_pct', label: 'Customers with evidence' },
                            { key: 'holding_product_catalog_match_pct', label: 'Holdings matched to catalog' },
                        ].map((row) => (
                            <Box key={row.key}>
                                <HStack justify="space-between" mb={1}>
                                    <Text fontSize="xs" color="gray.600">{row.label}</Text>
                                    <Text fontSize="xs" fontWeight="bold" color="gray.700">
                                        {(coverage[row.key] || 0).toFixed(1)}%
                                    </Text>
                                </HStack>
                                <Progress
                                    value={coverage[row.key] || 0}
                                    size="xs"
                                    borderRadius="full"
                                    colorScheme={coverage[row.key] >= 60 ? 'green' : coverage[row.key] >= 30 ? 'yellow' : 'red'}
                                />
                            </Box>
                        ))}
                    </SimpleGrid>

                    <Box mb={4}>
                        <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={2}>
                            Role readiness
                        </Text>
                        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={3}>
                            {roleReadiness.map((row) => (
                                <Box
                                    key={row.role}
                                    border="1px solid"
                                    borderColor="gray.100"
                                    borderRadius="lg"
                                    p={3}
                                    bg="white"
                                >
                                    <HStack justify="space-between" mb={1}>
                                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                            {row.role}
                                        </Text>
                                        <Badge colorScheme={STATUS_COLOR[row.status] || 'gray'} borderRadius="full">
                                            {row.status}
                                        </Badge>
                                    </HStack>
                                    {row.computed?.length > 0 && (
                                        <Text fontSize="xs" color="green.700">
                                            <b>Computed:</b> {row.computed.join(', ')}
                                        </Text>
                                    )}
                                    {row.inferred?.length > 0 && (
                                        <Text fontSize="xs" color="yellow.700">
                                            <b>Inferred:</b> {row.inferred.join(', ')}
                                        </Text>
                                    )}
                                    {row.unavailable?.length > 0 && (
                                        <Text fontSize="xs" color="red.600">
                                            <b>Unavailable:</b> {row.unavailable.join(', ')}
                                        </Text>
                                    )}
                                </Box>
                            ))}
                        </SimpleGrid>
                    </Box>

                    {nextBestUploads.length > 0 && (
                        <Box
                            bg="orange.50"
                            border="1px solid"
                            borderColor="orange.100"
                            borderRadius="lg"
                            p={3}
                        >
                            <HStack mb={2}>
                                <Icon as={FiAlertTriangle} color="orange.500" />
                                <Text fontSize="sm" fontWeight="semibold" color="orange.700">
                                    Next best uploads
                                </Text>
                            </HStack>
                            <VStack align="stretch" spacing={1}>
                                {nextBestUploads.map((message) => (
                                    <Text key={message} fontSize="xs" color="orange.800">• {message}</Text>
                                ))}
                            </VStack>
                        </Box>
                    )}
                </Box>
            )}

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} mb={5}>
                {DATASETS.map((dataset) => {
                    const result = uploadResults[dataset.type];
                    const isUploading = uploadingType === dataset.type;
                    const inputRef = (ref) => {
                        if (ref) {
                            fileInputRefs.current[dataset.type] = ref;
                        }
                    };
                    return (
                        <Box
                            key={dataset.type}
                            bg="white"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="xl"
                            p={5}
                            opacity={isUploading ? 0.7 : 1}
                        >
                            <HStack justify="space-between" align="start" mb={3}>
                                <HStack spacing={3} align="start">
                                    <Box
                                        bg="brand.tealSoft"
                                        borderRadius="lg"
                                        p={2}
                                        color="brand.navy"
                                    >
                                        <Icon as={dataset.icon} boxSize={5} />
                                    </Box>
                                    <Box>
                                        <Text fontWeight="semibold" color="gray.800">{dataset.title}</Text>
                                        <Text fontSize="xs" color="gray.500" mt={1}>{dataset.roleHint}</Text>
                                    </Box>
                                </HStack>
                                <Tag size="sm" colorScheme="gray" borderRadius="full">
                                    <TagLabel>{dataset.type}</TagLabel>
                                </Tag>
                            </HStack>

                            <Text fontSize="sm" color="gray.600" mb={3}>
                                {dataset.description}
                            </Text>

                            <Stack direction={{ base: 'column', md: 'row' }} spacing={2} mb={3}>
                                <Button
                                    leftIcon={<FiDownload />}
                                    size="sm"
                                    variant="outline"
                                    colorScheme="blue"
                                    onClick={() => handleTemplateDownload(dataset)}
                                >
                                    Download template
                                </Button>
                                <Button
                                    leftIcon={<FiUploadCloud />}
                                    size="sm"
                                    colorScheme="blue"
                                    isLoading={isUploading}
                                    loadingText="Uploading…"
                                    isDisabled={!canUpload}
                                    onClick={() => fileInputRefs.current[dataset.type]?.click()}
                                >
                                    Upload CSV / XLSX
                                </Button>
                                <Checkbox
                                    size="sm"
                                    isChecked={Boolean(replaceFlags[dataset.type])}
                                    onChange={(e) =>
                                        setReplaceFlags((prev) => ({ ...prev, [dataset.type]: e.target.checked }))
                                    }
                                    isDisabled={!canUpload}
                                >
                                    Replace existing rows
                                </Checkbox>
                                <input
                                    type="file"
                                    accept=".csv,.xlsx,.xls"
                                    ref={inputRef}
                                    style={{ display: 'none' }}
                                    onChange={(e) => handleFileSelected(dataset, e.target.files?.[0])}
                                />
                            </Stack>

                            {!canUpload && (
                                <Text fontSize="xs" color="gray.500">
                                    Uploads require the firm admin role on a Solo, Team or Business plan.
                                </Text>
                            )}

                            {result && (
                                <Box mt={3} p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.100">
                                    {result.error ? (
                                        <Alert status="error" borderRadius="md" variant="left-accent">
                                            <AlertIcon />
                                            <AlertDescription fontSize="xs">
                                                {result.filename ? `${result.filename}: ` : ''}{result.error}
                                            </AlertDescription>
                                        </Alert>
                                    ) : (
                                        <>
                                            <HStack spacing={2} mb={2} flexWrap="wrap">
                                                <Badge colorScheme="blue">
                                                    {formatCount(result.received_rows)} received
                                                </Badge>
                                                <Badge colorScheme="green">
                                                    {formatCount(result.accepted_rows)} accepted
                                                </Badge>
                                                <Badge colorScheme={result.rejected_rows > 0 ? 'red' : 'gray'}>
                                                    {formatCount(result.rejected_rows)} rejected
                                                </Badge>
                                            </HStack>
                                            {result.filename && (
                                                <Text fontSize="xs" color="gray.500">File: {result.filename}</Text>
                                            )}
                                            {result.warnings?.length > 0 && (
                                                <Box mt={2}>
                                                    <Text fontSize="xs" fontWeight="bold" color="orange.700" mb={1}>
                                                        Warnings ({result.warnings.length})
                                                    </Text>
                                                    <VStack align="stretch" spacing={0.5} maxH="100px" overflowY="auto">
                                                        {result.warnings.slice(0, 12).map((warning, idx) => (
                                                            <Text key={idx} fontSize="xs" color="gray.700">
                                                                {warning}
                                                            </Text>
                                                        ))}
                                                        {result.warnings.length > 12 && (
                                                            <Text fontSize="xs" color="gray.500">
                                                                +{result.warnings.length - 12} more warnings…
                                                            </Text>
                                                        )}
                                                    </VStack>
                                                </Box>
                                            )}
                                        </>
                                    )}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </SimpleGrid>

            {canSeeSignals && (
                <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" p={5}>
                    <HStack justify="space-between" mb={3}>
                        <HStack spacing={2}>
                            <Icon as={FiActivity} color="brand.navy" />
                            <Heading size="sm" color="gray.800">Live product-data signals</Heading>
                        </HStack>
                        <Text fontSize="xs" color="gray.500">
                            {signals?.total_signals != null ? `${signals.total_signals} total · top ${signals.signals?.length || 0} shown` : ''}
                        </Text>
                    </HStack>
                    {signalsLoading ? (
                        <HStack><Spinner size="sm" color="brand.teal" /><Text fontSize="sm" color="gray.600">Loading signals…</Text></HStack>
                    ) : !signals?.signals?.length ? (
                        <Text fontSize="sm" color="gray.500">
                            No active product, evidence, drift, or risk-mismatch signals. Upload datasets to populate this view.
                        </Text>
                    ) : (
                        <VStack align="stretch" spacing={2}>
                            {signals.signals.map((signal) => (
                                <Box
                                    key={signal.signal_id}
                                    border="1px solid"
                                    borderColor="gray.100"
                                    borderRadius="lg"
                                    p={3}
                                    bg="gray.50"
                                >
                                    <HStack justify="space-between" mb={1} flexWrap="wrap" gap={2}>
                                        <HStack spacing={2}>
                                            <Badge colorScheme={SEVERITY_COLOR[signal.severity] || 'gray'}>
                                                {signal.severity}
                                            </Badge>
                                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                {signal.title}
                                            </Text>
                                        </HStack>
                                        <Text fontSize="xs" color="gray.500">{signal.role}</Text>
                                    </HStack>
                                    <Text fontSize="xs" color="gray.600" mb={2}>{signal.description}</Text>
                                    {signal.recommended_action && (
                                        <Text fontSize="xs" color="brand.navy" mb={2}>
                                            <b>Recommended:</b> {signal.recommended_action}
                                        </Text>
                                    )}
                                    {signal.evidence && (
                                        <HStack spacing={2} flexWrap="wrap">
                                            {Object.entries(signal.evidence).map(([key, value]) => (
                                                <Tag key={key} size="sm" variant="subtle" colorScheme="gray">
                                                    <TagLabel>
                                                        {key}: {value === null || value === undefined ? '–' : String(value)}
                                                    </TagLabel>
                                                </Tag>
                                            ))}
                                        </HStack>
                                    )}
                                </Box>
                            ))}
                        </VStack>
                    )}
                </Box>
            )}

        </Box>
    );
};

export default FirmReadyDatasets;
